import { useEffect, useRef } from "react";
import { Box, Typography, TextField, IconButton, CircularProgress, Chip } from "@mui/material";
import { Send as SendIcon, AutoAwesome as SparkleIcon, Search as SearchIcon } from "@mui/icons-material";
import { colors, typography, borders, shadows } from "../../ui/colors";
import { t as ct } from "../../dashboard/coach/coachTokens";
import GuidedQuestion from "../../nutrition/ai/GuidedQuestion";

/** Collapse stripped-marker whitespace runs into clean prose. */
const tidyProse = (text) =>
    (text || "")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

const SearchResultsCard = ({ searches = [] }) => (
    <Box
        sx={{
            alignSelf: "center",
            bgcolor: ct.greenLight,
            border: `1px dashed ${ct.green}`,
            borderRadius: borders.radius.md,
            padding: "8px 12px",
            maxWidth: "90%",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <SearchIcon sx={{ fontSize: 14, color: ct.green }} />
            <Typography sx={{ ...typography.smallSemibold, color: ct.green }}>
                חיפש במאגר התרגילים
            </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {searches.map((s, i) => (
                <Chip
                    key={i}
                    label={`${s.query} · ${s.count} תוצאות`}
                    size="small"
                    sx={{
                        fontFamily: typography.fontFamily,
                        ...typography.tiny,
                        bgcolor: s.count > 0 ? colors.white : colors.errorBg,
                        color: s.count > 0 ? colors.textSecondary : colors.errorText,
                        border: `1px solid ${s.count > 0 ? colors.border : colors.error}`,
                        height: 22,
                    }}
                />
            ))}
        </Box>
    </Box>
);

const WorkoutAiConversation = ({
    messages,
    streamingText,
    statusText,
    isLoading,
    pendingQuestion,
    input,
    onInputChange,
    onSend,
    onAnswerQuestion,
}) => {
    const endRef = useRef(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, streamingText, statusText, pendingQuestion]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend?.();
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: colors.white }}>
            <Box
                sx={{
                    padding: "10px 16px",
                    borderBottom: borders.default,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: borders.radius.md,
                        background: `linear-gradient(135deg, ${ct.green} 0%, ${ct.greenDark} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <SparkleIcon sx={{ color: colors.white, fontSize: 18 }} />
                </Box>
                <Box>
                    <Typography sx={{ ...typography.bodySemibold, color: colors.textPrimary }}>
                        עוזר תוכניות אימון AI
                    </Typography>
                    <Typography sx={{ ...typography.tiny, color: colors.textMuted }}>
                        בונה תוכניות אימון מותאמות אישית
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    padding: "12px 16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    bgcolor: colors.bg,
                }}
            >
                {messages.length === 0 && !isLoading && !streamingText && (
                    <Box sx={{ textAlign: "center", mt: 3 }}>
                        <Typography sx={{ ...typography.body, color: colors.textSecondary }}>
                            שלום! אני אעזור לך לבנות תוכנית אימון.
                        </Typography>
                        <Typography sx={{ ...typography.small, color: colors.textMuted, mt: 1 }}>
                            כתוב "בנה לי תוכנית" כדי להתחיל, או פרט מה תרצה.
                        </Typography>
                    </Box>
                )}

                {messages.map((msg, idx) => {
                    if (msg.role === "system" && msg.kind === "search_results") {
                        return <SearchResultsCard key={idx} searches={msg.searches} />;
                    }
                    return (
                        <Box
                            key={idx}
                            sx={{
                                display: "flex",
                                justifyContent: msg.role === "user" ? "flex-start" : "flex-end",
                            }}
                        >
                            <Box
                                sx={{
                                    maxWidth: msg.role === "user" ? "85%" : "95%",
                                    padding: "8px 12px",
                                    borderRadius: borders.radius.lg,
                                    bgcolor: msg.role === "user" ? ct.green : colors.white,
                                    color: msg.role === "user" ? colors.white : colors.textPrimary,
                                    boxShadow: msg.role === "user" ? "none" : shadows.xs,
                                    border: msg.role === "user" ? "none" : borders.default,
                                }}
                            >
                                <Typography sx={{ ...typography.body, color: "inherit", whiteSpace: "pre-wrap" }}>
                                    {msg.role === "user" ? msg.content : tidyProse(msg.content)}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}

                {(isLoading || streamingText) && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Box
                            sx={{
                                maxWidth: "95%",
                                padding: "8px 12px",
                                borderRadius: borders.radius.lg,
                                bgcolor: colors.white,
                                boxShadow: shadows.xs,
                                border: borders.default,
                            }}
                        >
                            {statusText && !streamingText && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={12} sx={{ color: ct.green }} />
                                    <Typography sx={{ ...typography.small, color: colors.textMuted }}>
                                        {statusText}
                                    </Typography>
                                </Box>
                            )}
                            {streamingText && (
                                <Box sx={{ position: "relative" }}>
                                    <Typography sx={{ ...typography.body, color: colors.textPrimary, whiteSpace: "pre-wrap" }}>
                                        {tidyProse(streamingText)}
                                    </Typography>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "inline-block",
                                            width: 6,
                                            height: 14,
                                            bgcolor: ct.green,
                                            ml: 0.5,
                                            animation: "blink 1s infinite",
                                            verticalAlign: "text-bottom",
                                            "@keyframes blink": {
                                                "0%, 50%": { opacity: 1 },
                                                "51%, 100%": { opacity: 0 },
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {pendingQuestion && !isLoading && (
                    <Box
                        sx={{
                            padding: "10px 12px",
                            borderRadius: borders.radius.lg,
                            bgcolor: ct.greenLight,
                            border: `1px solid ${ct.green}`,
                        }}
                    >
                        <GuidedQuestion
                            question={pendingQuestion.question}
                            options={pendingQuestion.options}
                            onAnswer={onAnswerQuestion}
                            disabled={isLoading}
                        />
                    </Box>
                )}

                <div ref={endRef} />
            </Box>

            <Box
                sx={{
                    padding: "10px 12px",
                    borderTop: borders.default,
                    bgcolor: colors.white,
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-end",
                    flexShrink: 0,
                }}
            >
                <TextField
                    value={input}
                    onChange={(e) => onInputChange?.(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={pendingQuestion ? "או כתוב תשובה חופשית..." : "כתוב הודעה..."}
                    disabled={isLoading}
                    multiline
                    maxRows={3}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: borders.radius.lg,
                            fontFamily: typography.fontFamily,
                            ...typography.body,
                            bgcolor: colors.bg,
                        },
                    }}
                />
                <IconButton
                    onClick={() => onSend?.()}
                    disabled={isLoading || !input.trim()}
                    sx={{
                        bgcolor: ct.green,
                        color: colors.white,
                        borderRadius: borders.radius.md,
                        width: 36,
                        height: 36,
                        "&:hover": { bgcolor: ct.greenDark },
                        "&.Mui-disabled": { bgcolor: colors.border, color: colors.textMuted },
                    }}
                >
                    <SendIcon sx={{ fontSize: 18, transform: "rotate(180deg)" }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default WorkoutAiConversation;
